/**
 * Study Field Mapping
 *
 * CRITICAL FILE FOR SEARCH BUG FIX
 * Maps study names to their field/category for backend filtering
 *
 * This file solves the root cause: frontend was NOT sending studyField parameter
 * Backend requires ALL 4 params (studyLevel, studyField, studyName, studyYear) to filter
 */

import { MBO_NIVEAU_1_STUDIES } from '../../features/vacature-plaatsen/data/alleStudies/MBO-Niveau1.jsx'
import { MBO_NIVEAU_2_STUDIES } from '../../features/vacature-plaatsen/data/alleStudies/MBO-Niveau2.jsx'
import { MBO_NIVEAU_3_STUDIES } from '../../features/vacature-plaatsen/data/alleStudies/MBO-Niveau3.jsx'
import { MBO_NIVEAU_4_STUDIES } from '../../features/vacature-plaatsen/data/alleStudies/MBO-Niveau4.jsx'
import { HBO_ASSOCIATE_DEGREE_STUDIES } from '../../features/vacature-plaatsen/data/alleStudies/HBO-AssociateDegree.jsx'
import { HBO_BACHELOR_STUDIES } from '../../features/vacature-plaatsen/data/alleStudies/HBO-Bachelor.jsx'
import { HBO_MASTER_STUDIES } from '../../features/vacature-plaatsen/data/alleStudies/HBO-Master.jsx'

/**
 * Study data by level
 * Each level maps to nested object: Field → Subcategory → [Studies]
 * EXCEPT MBO Niveau 1 which is a flat array (no categories)
 */
const STUDY_DATA_BY_LEVEL = {
  'MBO Niveau 1': MBO_NIVEAU_1_STUDIES,  // Flat array
  'MBO Niveau 2': MBO_NIVEAU_2_STUDIES,  // Nested object
  'MBO Niveau 3': MBO_NIVEAU_3_STUDIES,  // Nested object
  'MBO Niveau 4': MBO_NIVEAU_4_STUDIES,  // Nested object
  'HBO Associate Degree': HBO_ASSOCIATE_DEGREE_STUDIES,  // Nested object
  'HBO Bachelor': HBO_BACHELOR_STUDIES,  // Nested object
  'HBO Master': HBO_MASTER_STUDIES  // Nested object
}

/**
 * Build study → field mapping dynamically
 *
 * Structure: { studyName: { level: fieldName } }
 * Example: { "Finance & Control": { "HBO Bachelor": "Economie & Bedrijfskunde" } }
 *
 * Special case: MBO Niveau 1 has no categories, so fieldName will be null
 */
function buildStudyFieldMap() {
  const map = {}

  for (const [level, studyData] of Object.entries(STUDY_DATA_BY_LEVEL)) {
    if (!studyData) continue

    // Handle MBO Niveau 1 (flat array - no categories)
    if (Array.isArray(studyData)) {
      for (const study of studyData) {
        if (!study || typeof study !== 'string') continue

        if (!map[study]) {
          map[study] = {}
        }

        // MBO Niveau 1 has no categories/fields
        map[study][level] = null
      }
      continue
    }

    // Handle nested structure (MBO 2-4, HBO, Universiteit)
    if (typeof studyData === 'object') {
      // Iterate through fields (top-level keys)
      for (const [field, subcategories] of Object.entries(studyData)) {
        if (!subcategories || typeof subcategories !== 'object') continue

        // Iterate through subcategories
        for (const [subcategory, studies] of Object.entries(subcategories)) {
          if (!Array.isArray(studies)) continue

          // Map each study to its field
          for (const study of studies) {
            if (!study || typeof study !== 'string') continue

            // Initialize study entry if not exists
            if (!map[study]) {
              map[study] = {}
            }

            // Store field for this level
            map[study][level] = field
          }
        }
      }
    }
  }

  return map
}

// Build the mapping once
const STUDY_FIELD_MAP = buildStudyFieldMap()

/**
 * Get field/category for a study name and level
 *
 * THIS IS THE CRITICAL FUNCTION THAT FIXES THE BUG
 *
 * @param {string} studyName - Study name (e.g., "Finance & Control")
 * @param {string} studyLevel - Study level (e.g., "HBO Bachelor")
 * @returns {string|null} Field name (e.g., "Economie & Bedrijfskunde") or null if not found
 *
 * @example
 * getFieldForStudy("Finance & Control", "HBO Bachelor")
 * // Returns: "Economie & Bedrijfskunde"
 *
 * getFieldForStudy("Software Development", "HBO Associate Degree")
 * // Returns: "Exact & Informatica"
 */
export const getFieldForStudy = (studyName, studyLevel) => {
  if (!studyName || !studyLevel) {
    console.warn('⚠️  getFieldForStudy: studyName and studyLevel are required')
    return null
  }

  const studyEntry = STUDY_FIELD_MAP[studyName]

  if (!studyEntry) {
    console.warn(`⚠️  getFieldForStudy: Study "${studyName}" not found in mapping`)
    return null
  }

  const field = studyEntry[studyLevel]

  if (!field) {
    console.warn(`⚠️  getFieldForStudy: No field found for "${studyName}" at level "${studyLevel}"`)
    return null
  }

  return field
}

/**
 * Get all fields for a given study level
 * Useful for validation and debugging
 *
 * @param {string} studyLevel - Study level (e.g., "HBO Bachelor")
 * @returns {string[]} Array of field names
 *
 * @example
 * getFieldsForLevel("HBO Bachelor")
 * // Returns: ["Economie & Bedrijfskunde", "Techniek & ICT", ...]
 */
export const getFieldsForLevel = (studyLevel) => {
  const studyData = STUDY_DATA_BY_LEVEL[studyLevel]

  if (!studyData) {
    console.warn(`⚠️  getFieldsForLevel: Level "${studyLevel}" not found`)
    return []
  }

  return Object.keys(studyData)
}

/**
 * Check if a study exists in the mapping
 *
 * @param {string} studyName - Study name
 * @param {string} studyLevel - Study level
 * @returns {boolean} True if study exists for that level
 */
export const studyExists = (studyName, studyLevel) => {
  return Boolean(STUDY_FIELD_MAP[studyName]?.[studyLevel])
}

/**
 * Get all studies for a given field and level
 *
 * @param {string} field - Field name (e.g., "Economie & Bedrijfskunde")
 * @param {string} studyLevel - Study level
 * @returns {string[]} Array of study names
 */
export const getStudiesForField = (field, studyLevel) => {
  const studyData = STUDY_DATA_BY_LEVEL[studyLevel]

  if (!studyData || !studyData[field]) {
    return []
  }

  const studies = []
  const subcategories = studyData[field]

  for (const subcategoryStudies of Object.values(subcategories)) {
    if (Array.isArray(subcategoryStudies)) {
      studies.push(...subcategoryStudies)
    }
  }

  return studies
}

// Export the raw map for debugging
export const _STUDY_FIELD_MAP_DEBUG = STUDY_FIELD_MAP
